// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useCallback, useState } from "react";
import { Form } from "antd";

// -----------------------------------------------------------------------------
// Store
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { FormInput } from "components/Common/Form/FormInput";
import { FormTextArea } from "components/Common/Form/FormTextArea";
import { validateRequiredEncodedLength, validateEncodedLength } from "helpers/Form/commonFormValidations";
// -----------------------------------------------------------------------------
// Utils and Styles
// -----------------------------------------------------------------------------
import StyledScheduleLiveForm from "./styles";
import { useGetDomainGymQuery, useGetCoachesQuery, useCreateCoachMutation } from "store/api/api";
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import { useParams } from "react-router";
import { decodeString, encodeString, sortByProperty, validTrainers } from "utils";
import { workoutTypes } from "helpers/constants";
import { FormTagSelect } from "components/Common/Form/FormTagSelect";
// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const ScheduleLiveForm = ({ nextStep, values, loading, preRecordedLive }) => {
	const [form] = Form.useForm();
	const user = useRecoilValue(userAtom);
	const { gym } = useParams();
	const domainGymResponse = useGetDomainGymQuery(gym);
	const [createCoachTrigger, createCoach] = useCreateCoachMutation();
	const domainGymId = domainGymResponse.data?.id;
	const [error, setError] = useState();
	const {
		data: coachesData,
		isLoading: isCoachesLoading,
		refetch,
	} = useGetCoachesQuery(domainGymId, {
		skip: !domainGymId,
	});

	const onFinish = useCallback(async () => {
		if (error) {
			return;
		}
		const formData = form.getFieldsValue();
		const { workoutCoach, workoutShortDescription, workoutDescription, workoutTitle } = formData;
		const coaches = await validTrainers({
			domainGymId,
			coachesData,
			workoutCoach,
			createCoachTrigger,
		});
		await refetch();
		const payload = {
			gym_id: domainGymId,
			user_id: user?.id,
			name: encodeString(workoutTitle),
			coaches,
			description: encodeString(workoutDescription),
			short_description: encodeString(workoutShortDescription),
			...(!preRecordedLive && { type: workoutTypes.SCHEDULED_LIVE_STREAM }),
			is_free: 0,
		};
		await nextStep(payload);
	}, [error, form, domainGymId, coachesData, createCoachTrigger, refetch, user?.id, preRecordedLive, nextStep]);

	const onChangeTitle = (e) => {
		const value = e.target?.value;
		const error = validateRequiredEncodedLength(value, 255);
		setError(error || "");
	};

	const memoizedCoachOptions = React.useMemo(() => {
		return (
			sortByProperty(coachesData?.coaches, "name")
				?.filter(({ status }) => status === "active")
				?.map((coach) => ({
					label: `${decodeString(coach?.name)}`,
					value: coach?.id,
				})) || []
		);
	}, [coachesData]);

	return (
		<StyledScheduleLiveForm>
			<div className="sb-schedule-live-form">
				<Form
					onFinish={onFinish}
					layout="vertical"
					className="sb-schedule-live-form__form"
					form={form}
					initialValues={{
						workoutTitle: decodeString(values.name),
						workoutDescription: decodeString(values.description),
						workoutCoach: values.coaches,
						workoutShortDescription: values.short_description,
					}}
					requiredMark
				>
					<FormInput
						name="workoutTitle"
						rules={[
							() => ({
								required: true,
								validator(_, value) {
									const validationStatus = validateRequiredEncodedLength(value, 255);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						onChange={onChangeTitle}
						label="Workout Title"
						validateStatus={error ? "error" : undefined}
						size="small"
						help={error}
						autoFocus
					/>
					<FormTextArea
						name="workoutShortDescription"
						label="Workout Short Description"
						placeholder="Workout Short Description"
						rules={[
							() => ({
								validator(_, value) {
									const validationStatus = validateEncodedLength(value, 100);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						rows={2}
					/>
					<FormTextArea
						name="workoutDescription"
						label="Workout Description"
						placeholder="Workout Description"
						rules={[
							() => ({
								required: true,
								validator(_, value) {
									const validationStatus = validateRequiredEncodedLength(value, 2048);
									if (validationStatus) {
										return Promise.reject(new Error(validationStatus));
									}
									return Promise.resolve();
								},
							}),
						]}
						rows={4}
					/>
					{coachesData?.coaches && (
						<FormTagSelect
							name="workoutCoach"
							options={memoizedCoachOptions}
							label="Workout Trainer"
							placeholder="Workout Trainer"
							rules={[
								{ required: true, message: "This field is required" },
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const longTags = value.filter((tag) => tag.length >= 256);
											if (longTags.length > 0) {
												return Promise.reject(new Error(`Maximum allowed characters is 255 per trainer.`));
											}
										}
										return Promise.resolve();
									},
								}),
								() => ({
									required: true,
									validator(_, value) {
										if (value && Array.isArray(value)) {
											const inactiveCoaches = coachesData?.coaches
												.filter((coach) => coach.status !== "active")
												.map((coach) => coach.name.toLowerCase());
											const invalidTags = value.filter((tag) => inactiveCoaches.includes(tag.toLowerCase()));
											if (invalidTags.length > 0) {
												return Promise.reject(
													new Error(`Select inactive trainers is not allowed. (${invalidTags.join(", ")})`)
												);
											}
										}
										return Promise.resolve();
									},
								}),
							]}
							showSearch
							loading={isCoachesLoading}
						/>
					)}
					<Button
						type="submit"
						className="sb-schedule-live-form__form__button primary-filled"
						disabled={loading}
						loading={createCoach.isLoading || loading}
						data-id="btnSubmit"
					>
						Next
					</Button>
				</Form>
			</div>
		</StyledScheduleLiveForm>
	);
};

export default ScheduleLiveForm;

